import type {
  ShadowConfig,
  FillConfig,
  BorderConfig,
  TextShadowConfig,
  GradientConfig,
  LineElement,
  PathElement,
  PathAnchor,
} from '../types';
import { anchorsToPathData } from './pathUtils';

export const defaultFill = (color: string = '#ffffff'): FillConfig => ({
  type: 'solid',
  color,
});

export const defaultGradient = (
  color1: string = '#3b82f6',
  color2: string = '#06b6d4'
): GradientConfig => ({
  type: 'linear',
  angle: 180,
  stops: [
    { offset: 0, color: color1 },
    { offset: 1, color: color2 },
  ],
});

export const defaultShadow = (): ShadowConfig => ({
  enabled: false,
  color: '#00000066',
  offsetX: 4,
  offsetY: 4,
  blur: 8,
});

export const defaultTextShadow = (): TextShadowConfig => ({
  enabled: false,
  color: '#00000088',
  offsetX: 1,
  offsetY: 1,
  blur: 2,
});

export const defaultBorder = (
  color: string = '#ffffff33',
  width: number = 0
): BorderConfig => ({
  color,
  width,
  style: 'solid',
  radius: {
    topLeft: 0,
    topRight: 0,
    bottomRight: 0,
    bottomLeft: 0,
    linked: true,
  },
});

export const resolveElementFill = (element: Record<string, unknown>): FillConfig => {
  if (element.shapeFill && typeof element.shapeFill === 'object') {
    return element.shapeFill as FillConfig;
  }
  if (element.textFill && typeof element.textFill === 'object') {
    return element.textFill as FillConfig;
  }
  if (element.buttonFill && typeof element.buttonFill === 'object') {
    return element.buttonFill as FillConfig;
  }
  if (typeof element.backgroundColor === 'string') {
    return defaultFill(element.backgroundColor);
  }
  if (typeof element.fill === 'string') {
    return defaultFill(element.fill);
  }
  if (typeof element.color === 'string') {
    return defaultFill(element.color);
  }
  return defaultFill();
};

export const resolveElementBorder = (element: Record<string, unknown>): BorderConfig => {
  if (element.border && typeof element.border === 'object') {
    return element.border as BorderConfig;
  }
  return {
    color: (element.stroke as string) ?? '#ffffff33',
    width: (element.strokeWidth as number) ?? 0,
    style: 'solid',
    radius: {
      topLeft: (element.borderRadius as number) ?? 0,
      topRight: (element.borderRadius as number) ?? 0,
      bottomRight: (element.borderRadius as number) ?? 0,
      bottomLeft: (element.borderRadius as number) ?? 0,
      linked: true,
    },
  };
};

export const resolveElementShadow = (element: Record<string, unknown>): ShadowConfig => {
  if (element.shadow && typeof element.shadow === 'object') {
    return element.shadow as ShadowConfig;
  }
  return defaultShadow();
};

export const createDefaultLine = (overrides?: Partial<LineElement>): LineElement => ({
  id: crypto.randomUUID(),
  type: 'line',
  name: 'New Line',
  x: 0,
  y: 0,
  width: 200,
  height: 0,
  rotation: 0,
  opacity: 1,
  locked: false,
  visible: true,
  zIndex: 0,
  layer: 'BACKGROUND_LAYER',
  contentType: 'STATIC',
  x1: 0,
  y1: 0,
  x2: 200,
  y2: 0,
  stroke: '#ffffff',
  strokeWidth: 2,
  strokeDashStyle: 'solid',
  arrowStart: 'none',
  arrowEnd: 'none',
  lineCap: 'round',
  ...overrides,
});

export const createDefaultPath = (anchors: PathAnchor[], closed: boolean, overrides?: Partial<PathElement>): PathElement => ({
  id: crypto.randomUUID(),
  type: 'path',
  name: 'New Path',
  x: 0,
  y: 0,
  width: 1,
  height: 1,
  rotation: 0,
  opacity: 1,
  locked: false,
  visible: true,
  zIndex: 0,
  layer: 'BACKGROUND_LAYER',
  contentType: 'STATIC',
  pathData: anchorsToPathData(anchors, closed),
  anchors,
  closed,
  stroke: '#ffffff',
  strokeWidth: 2,
  strokeDashStyle: 'solid',
  lineCap: 'round',
  lineJoin: 'miter',
  fill: 'transparent',
  fillOpacity: 0,
  ...overrides,
});

export const getDashArray = (style: string, strokeWidth: number): number[] => {
  switch (style) {
    case 'dashed':
      return [strokeWidth * 4, strokeWidth * 3];
    case 'dotted':
      return [strokeWidth, strokeWidth * 2];
    case 'dash-dot':
      return [strokeWidth * 4, strokeWidth * 2, strokeWidth, strokeWidth * 2];
    case 'solid':
    default:
      return [];
  }
};
